import styled from "@emotion/styled";
import {DefaultColors} from "@cirrus/legacy/app-components/app/styles/colors/colors-default";

export const ContainerDiv = styled("div")({
  // position: "absolute",
  // border: "1px solid white",
  // left: "10%",
  // width: "70%",
  // height: "500px",
  // overflow: "auto",
})

export const ViewerDiv = styled.div`
  
  position: relative;
  text-align: center;

  /* Modern pdf.js declares the highlight colour variables on .highlight itself
   * (see the .textLayer .highlight rule below), so the v3-era :root
   * declarations that used to live here have been removed. The magenta/green
   * project colours are preserved on .highlight. */

  .page {
    /* --total-scale-factor drives ALL viewer geometry — the page box
     * sizing (via the inline round() expression pdf.js sets on the page div),
     * text-layer span font-size, and annotation sizing. pdf.js defines it on
     * .pdfViewer .page in its own pdf_viewer.css, which we don't import.
     * Without this definition the var is undefined everywhere: span font-size
     * falls back to the inherited dialog font and search highlights land on
     * the wrong text. --scale-factor itself is set inline on the viewer
     * element by PDFViewer. */
    --user-unit: 1;
    --total-scale-factor: calc(var(--scale-factor) * var(--user-unit));
    --scale-round-x: 1px;
    --scale-round-y: 1px;

    display: inline-block;
    padding: 0;
    direction: ltr;
    width: 816px;
    height: 1056px;
    margin: var(--page-margin);
    position: relative;
    overflow: visible;
    border: var(--page-border);
    background-clip: content-box;
    background-color: rgba(255, 255, 255, 1);
  }

  /* pdf_viewer.css rule omitted from the partial CSS copy: without it the
   * rendered canvas overruns the .page box by a few px at certain scales
   * (e.g. after a "page-width" refit on resize). That stray overflow makes
   * pdf.js' scrollIntoView treat .page as the scroll container and stop there,
   * so search-match scrolling silently no-ops. Constraining the wrapper to the
   * page box keeps scrolling working. */
  .canvasWrapper {
    overflow: hidden;
    width: 100%;
    height: 100%;
  }

  /* Modern pdf.js no longer sizes the canvas with inline px styles — it relies
   * on this rule from its own pdf_viewer.css. Without it the canvas displays at
   * its raw pixel-buffer size (CSS size × devicePixelRatio), overflowing the
   * .page box on scaled displays (e.g. Windows 125% → canvas 25% too large),
   * which misaligns the canvas glyphs against the text layer and search
   * highlights. */
  .canvasWrapper canvas {
    position: absolute;
    top: 0;
    left: 0;
    margin: 0;
    display: block;
    width: 100%;
    height: 100%;
    contain: content;
  }

  /* pdf_viewer.css rule omitted from the partial CSS copy. Modern pdf.js
   * renders an .annotationLayer sibling to .canvasWrapper in every .page. Upstream
   * positions it absolutely (overlapping the canvas); without this rule it
   * stays in normal flow and stacks BELOW the canvas, doubling the .page
   * content height (e.g. 556px box -> 1112px scrollHeight). That makes .page
   * report as scrollable, so pdf.js' scrollIntoView / scale-restore stop at
   * .page (a non-scroller) instead of walking up to .pdf-viewer — breaking
   * search-match scrolling and zoom page-anchoring. Keeping it out of flow
   * restores both. (.canvasWrapper overflow:hidden alone is not enough here:
   * it constrains the canvas, not this layer.) */
  .annotationLayer {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    transform-origin: 0 0;
  }

  /* Text layer rules below are vendored from pdfjs-dist's web/pdf_viewer.css
   * (.textLayer block) — verified identical in v5 and v6. Both changed the
   * text-layer DOM & CSS vs v3: the layer is now fully opaque (opacity:1) and
   * spans are made invisible via color:transparent only; span positioning is
   * driven by the --total-scale-factor / --min-font-size custom properties
   * pdf.js sets inline per page. The project's magenta/green highlight colours
   * are mapped onto the per-.highlight --highlight-bg-color /
   * --highlight-selected-bg-color (note: pdf.js declares these locally on
   * .highlight, NOT on :root). */
  .textLayer {
    color-scheme: only light;

    position: absolute;
    text-align: initial;
    inset: 0;
    overflow: clip;
    opacity: 1;
    line-height: 1;
    letter-spacing: normal;
    word-spacing: normal;
    text-size-adjust: none;
    forced-color-adjust: none;
    transform-origin: 0 0;
    caret-color: CanvasText;
    z-index: 0;
  }

  .textLayer.highlighting {
    touch-action: none;
  }

  .textLayer span,
  .textLayer br {
    color: transparent;
    position: absolute;
    white-space: pre;
    cursor: text;
    transform-origin: 0% 0%;
    user-select: text;
  }

  /* v6 span positioning: pdf.js sets --total-scale-factor / --font-height /
   * --scale-x / --rotate inline on each span; these rules turn those into the
   * actual font-size and transform. Without them search highlights and text
   * selection are mispositioned. */
  .textLayer {
    --min-font-size: 1;
    --text-scale-factor: calc(var(--total-scale-factor) * var(--min-font-size));
    --min-font-size-inv: calc(1 / var(--min-font-size));
  }

  .textLayer > :not(.markedContent),
  .textLayer .markedContent span:not(.markedContent) {
    z-index: 1;

    --font-height: 0;
    font-size: calc(var(--text-scale-factor) * var(--font-height));

    --scale-x: 1;
    --rotate: 0deg;
    transform: rotate(var(--rotate)) scaleX(var(--scale-x)) scale(var(--min-font-size-inv));
  }

  .textLayer .markedContent {
    display: contents;
  }

  .textLayer span[role="img"] {
    user-select: none;
    cursor: default;
  }

  .textLayer .highlight {
    /* Project highlight colours, mapped onto pdf.js' per-.highlight variables.
     * 0.25 alpha because the text layer itself is now fully opaque (v3 instead
     * dimmed the whole layer with opacity:0.25). */
    --highlight-bg-color: rgba(180, 0, 170, 0.25);
    --highlight-selected-bg-color: rgba(0, 100, 0, 0.25);
    --highlight-backdrop-filter: none;
    --highlight-selected-backdrop-filter: none;

    margin: -1px;
    padding: 1px;
    background-color: var(--highlight-bg-color);
    backdrop-filter: var(--highlight-backdrop-filter);
    border-radius: 4px;
  }

  @media screen and (forced-colors: active) {
    .textLayer .highlight {
      --highlight-bg-color: transparent;
      --highlight-selected-bg-color: transparent;
      --highlight-backdrop-filter: var(--hcm-highlight-filter);
      --highlight-selected-backdrop-filter: var(--hcm-highlight-selected-filter);
    }
  }

  .textLayer .highlight.appended {
    position: initial;
  }

  .textLayer .highlight.begin {
    border-radius: 4px 0 0 4px;
  }

  .textLayer .highlight.end {
    border-radius: 0 4px 4px 0;
  }

  .textLayer .highlight.middle {
    border-radius: 0;
  }

  .textLayer .highlight.selected {
    background-color: var(--highlight-selected-bg-color);
    backdrop-filter: var(--highlight-selected-backdrop-filter);
    scroll-margin-top: 50px;
  }

  /* The vendored text-layer copy (textLayer.css) sets .textLayer ::selection to
   * transparent, so without this rule dragging to select shows NO highlight at
   * all — the text layer is opaque-but-invisible over the canvas. v3 had a
   * visible selection (background: AccentColor) shown through the old
   * opacity:0.25 layer; the modern layer is opacity:1, so the colour must be
   * translucent here or it would paint over the canvas glyphs. */
  .textLayer ::selection {
    background: rgba(0, 100, 255, 0.3);
  }

  .textLayer br::selection {
    background: transparent;
  }

  .textLayer .endOfContent {
    display: block;
    position: absolute;
    inset: 100% 0 0;
    z-index: 0;
    cursor: default;
    user-select: none;
  }

  .textLayer.selecting .endOfContent {
    top: 0;
  }

`

export const ErrorContainer = styled.div`
  margin: 0 0 3rem;
  background-color: ${DefaultColors.gray_950};
  border: 1px solid ${DefaultColors.gray_930};
  border-radius: 5px;
`

export const ErrorMessageText = styled.p`
  margin: 0;
  padding: 2.5rem 1.75rem;
  font-size: 1.5rem;
  line-height: normal;
  color: ${DefaultColors.red_500};
  font-weight: 700;
  text-align: center;
`

/* Renders the underlying pdf.js error when debugging is enabled — the only way
 * to read it inside Safe Exam Browser, which has no console. Must stay
 * selectable so it can be copied out of a locked-down browser, and must wrap:
 * stack traces are long and the dialog does not scroll horizontally. */
export const ErrorReportPre = styled.pre`
  margin: 0 1.75rem 2rem;
  padding: 1rem;
  max-height: 20rem;
  overflow: auto;
  background-color: ${DefaultColors.gray_930};
  border-radius: 4px;
  font-family: ui-monospace, Consolas, monospace;
  font-size: 1.1rem;
  line-height: 1.4;
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  user-select: text;
`

